import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { KeyRound, Printer } from "lucide-react";
import { API } from "@/lib/api";
import { logError } from "@/lib/log";
import { formatDate } from "@/lib/format";
import { ProfitLossStatement } from "@/components/statements/ProfitLossStatement";
import { BalanceSheet } from "@/components/statements/BalanceSheet";
import { TrialBalanceTable } from "@/components/journal/TrialBalanceTable";
import { CashStatementView } from "@/components/statements/CashStatement";

const RANGES = [
  { key: "month", label: "This month" },
  { key: "3m", label: "3 months" },
  { key: "6m", label: "6 months" },
  { key: "ytd", label: "Year to date" },
];

function PinGate({ wrongPin, onSubmit }) {
  const [value, setValue] = useState("");
  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-6">
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(value); }}
        className="border border-[#1A1F16] bg-white p-10 max-w-md w-full text-center"
        data-testid="shared-pin-gate"
      >
        <KeyRound className="w-6 h-6 mx-auto text-[#A67C00]" />
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565] block mt-3">Protected statements</span>
        <h1 className="font-serif text-4xl tracking-tight mt-2">Enter the PIN.</h1>
        <p className="text-sm text-[#6C7565] mt-3">This link is PIN-protected. Ask the ledger owner for the 4–6 digit PIN.</p>
        <input
          data-testid="shared-pin-input"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          placeholder="••••"
          className="mt-6 w-40 mx-auto block bg-transparent border-b-2 border-[#1A1F16] py-2 text-center font-serif text-3xl tracking-[0.4em] focus:outline-none"
        />
        {wrongPin && <p className="text-xs text-[#D94F3D] mt-3" data-testid="shared-pin-error">Wrong PIN — try again.</p>}
        <button
          type="submit"
          data-testid="shared-pin-submit"
          disabled={value.length < 4}
          className="mt-6 bg-[#1A1F16] text-[#F7F5F0] px-8 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] disabled:opacity-40"
        >View statements</button>
      </form>
    </div>
  );
}

/** PUBLIC read-only statements view for CAs — no login, token-gated by the backend. */
export default function SharedStatements() {
  const { token } = useParams();
  const [range, setRange] = useState("ytd");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false); // false | "expired" | "pin"
  const [pin, setPin] = useState("");
  const [wrongPin, setWrongPin] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ range });
      if (pin) params.set("pin", pin);
      const { data } = await axios.get(`${API}/shared/${token}?${params.toString()}`);
      setData(data);
      setError(false);
      setWrongPin(false);
    } catch (e) {
      if (e.response?.status === 401) {
        setWrongPin(e.response?.data?.detail === "wrong_pin");
        setError("pin");
      } else {
        logError("Shared statements load failed", e);
        setError("expired");
      }
    }
  }, [token, range, pin]);
  useEffect(() => { load(); }, [load]);

  if (error === "pin") {
    return <PinGate wrongPin={wrongPin} onSubmit={setPin} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-6">
        <div className="border border-[#1A1F16] bg-white p-10 max-w-md text-center" data-testid="shared-link-invalid">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D94F3D]">Access denied</span>
          <h1 className="font-serif text-4xl tracking-tight mt-3">This link has expired.</h1>
          <p className="text-sm text-[#6C7565] mt-4">The share link is invalid, expired or has been revoked. Ask the ledger owner for a fresh link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1A1F16]">
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8 print:space-y-4">
        <div className="flex items-end justify-between border-b-2 border-[#1A1F16] pb-6 flex-wrap gap-4">
          <div>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A67C00]">Fin·Ledger — shared statements</span>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2" data-testid="shared-owner-name">
              {data ? `${data.owner_name}'s ledger.` : "Loading…"}
            </h1>
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#6C7565] mt-2" data-testid="shared-period">
              {data && `Period: ${formatDate(data.range_start)} — ${formatDate(data.range_end)} · Read-only`}
            </p>
          </div>
          <button
            data-testid="shared-print-btn"
            onClick={() => window.print()}
            className="border border-[#1A1F16] px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] flex items-center gap-2 print:hidden"
          >
            <Printer className="w-3.5 h-3.5" /> Print / PDF
          </button>
        </div>

        <div className="flex gap-2 flex-wrap print:hidden" data-testid="shared-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`shared-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                range === r.key ? "bg-[#1A1F16] text-[#F7F5F0] border-[#1A1F16]" : "border-[#D8D4C7] text-[#6C7565] hover:border-[#1A1F16]"
              }`}
            >{r.label}</button>
          ))}
        </div>

        <CashStatementView data={data?.cash_statement} />

        <div className="grid lg:grid-cols-2 gap-8 print:grid-cols-1">
          <ProfitLossStatement pnl={data?.pnl} />
          <BalanceSheet bs={data?.balance_sheet} />
        </div>
        <TrialBalanceTable data={data?.trial_balance} />

        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#6C7565] pb-8">
          Generated by FinLedger · Figures in ₹ · {data && `Link expires ${formatDate(data.expires_at)}`}
        </p>
      </div>
    </div>
  );
}
